import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Select,
  Spinner,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@elkaso-app/web-design';
import { useGetSourceBrandLocationsApi } from 'apis/crm-source-brand-locations/use-get-source-brand-locations-api';
import { useUpdateSourceBrandLocationApi } from 'apis/crm-source-brand-locations/use-update-source-brand-location-api';
import React, { useEffect, useState } from 'react';
import { InteractiveMap } from '../maps';

interface EditSourceBrandLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: number;
}

interface FormData {
  geoLocationId: number | '';
  sourceUrl: string;
  name: string;
  address: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  rating: number | '';
  ratingsTotal: number | '';
  reviewsTotal: number | '';
  priceLevel: number | '';
  avgCostPerCouple: number | '';
  avgCostCurrency: string;
  businessStatus: string;
  phoneNumber: string;
  website: string;
}

const EditSourceBrandLocationModal: React.FC<EditSourceBrandLocationModalProps> = ({ isOpen, onClose, locationId }) => {
  const toast = useToast();
  const updateMutation = useUpdateSourceBrandLocationApi();
  const { data: locationsData, isLoading: isLoadingLocation } = useGetSourceBrandLocationsApi();

  const [formData, setFormData] = useState<FormData>({
    geoLocationId: '',
    sourceUrl: '',
    name: '',
    address: '',
    coordinates: {
      lat: 0,
      lng: 0,
    },
    rating: '',
    ratingsTotal: '',
    reviewsTotal: '',
    priceLevel: '',
    avgCostPerCouple: '',
    avgCostCurrency: '',
    businessStatus: '',
    phoneNumber: '',
    website: '',
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});

  // Find the location data
  const location = locationsData?.data?.find((loc) => loc.id === locationId);

  // Populate form with existing data
  useEffect(() => {
    if (location) {
      setFormData({
        geoLocationId: location.geoLocation?.id || '',
        sourceUrl: location.sourceUrl || '',
        name: location.name || '',
        address: location.address || '',
        coordinates: {
          lat: location.coordinates?.lat || 0,
          lng: location.coordinates?.lng || 0,
        },
        rating: location.rating || '',
        ratingsTotal: location.ratingsTotal || '',
        reviewsTotal: location.reviewsTotal || '',
        priceLevel: location.priceLevel || '',
        avgCostPerCouple: location.avgCostPerCouple || '',
        avgCostCurrency: location.avgCostCurrency || '',
        businessStatus: location.businessStatus || '',
        phoneNumber: location.phoneNumber || '',
        website: location.website || '',
      });
    }
  }, [location]);

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.name) newErrors.name = 'Location name is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        status: 'error',
      });
      return;
    }

    try {
      const payload = {
        ...(formData.geoLocationId && { geoLocationId: Number(formData.geoLocationId) }),
        ...(formData.sourceUrl && { sourceUrl: formData.sourceUrl }),
        name: formData.name,
        ...(formData.address && { address: formData.address }),
        ...(formData.coordinates && {
          coordinates: {
            lat: formData.coordinates.lat,
            lng: formData.coordinates.lng,
          },
        }),
        ...(formData.rating && { rating: Number(formData.rating) }),
        ...(formData.ratingsTotal && { ratingsTotal: Number(formData.ratingsTotal) }),
        ...(formData.reviewsTotal && { reviewsTotal: Number(formData.reviewsTotal) }),
        ...(formData.priceLevel && { priceLevel: Number(formData.priceLevel) }),
        ...(formData.avgCostPerCouple && { avgCostPerCouple: Number(formData.avgCostPerCouple) }),
        ...(formData.avgCostCurrency && { avgCostCurrency: formData.avgCostCurrency }),
        ...(formData.businessStatus && { businessStatus: formData.businessStatus }),
        ...(formData.phoneNumber && { phoneNumber: formData.phoneNumber }),
        ...(formData.website && { website: formData.website }),
      };

      await updateMutation.mutateAsync({
        id: locationId,
        data: payload,
      });
      onClose();
    } catch (error) {
      // Error is handled in the API hook
    }
  };

  if (isLoadingLocation) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size='xl'>
        <ModalOverlay />
        <ModalContent maxW='800px'>
          <ModalBody>
            <VStack spacing={4} py={8}>
              <Spinner size='lg' />
              <Text>Loading location data...</Text>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  if (!location) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size='md'>
        <ModalOverlay />
        <ModalContent>
          <ModalBody>
            <Alert status='error'>
              <AlertIcon />
              Location not found
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='xl'>
      <ModalOverlay />
      <ModalContent maxW='800px'>
        <ModalHeader>Edit Source Brand Location</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align='stretch'>
            <Alert status='info'>
              <AlertIcon />
              Update the location information below.
            </Alert>

            {/* Read-only Information */}
            <VStack
              spacing={4}
              align='stretch'
              p={4}
              border='1px'
              borderColor='gray.200'
              borderRadius='md'
              bg='gray.50'>
              <Text fontWeight='bold' color='gray.700'>
                Read-Only Information
              </Text>

              <HStack spacing={4}>
                <Text fontSize='sm'>
                  <strong>Brand:</strong> {location.brand.name}
                </Text>
                <Text fontSize='sm'>
                  <strong>Source Platform:</strong> {location.sourcePlatform}
                </Text>
                <Text fontSize='sm'>
                  <strong>External ID:</strong> {location.sourceExternalId}
                </Text>
              </HStack>
            </VStack>

            {/* Editable Information */}
            <VStack spacing={4} align='stretch' p={4} border='1px' borderColor='gray.200' borderRadius='md'>
              <Text fontWeight='bold' color='gray.700'>
                Editable Information
              </Text>

              <HStack spacing={4}>
                <FormControl>
                  <FormLabel>Geo Location ID</FormLabel>
                  <NumberInput
                    value={formData.geoLocationId}
                    onChange={(_, valueAsNumber) =>
                      setFormData((prev) => ({ ...prev, geoLocationId: isNaN(valueAsNumber) ? '' : valueAsNumber }))
                    }>
                    <NumberInputField placeholder='Enter geo location ID' />
                  </NumberInput>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.name}>
                  <FormLabel>Location Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder='Enter location name'
                  />
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel>Source URL</FormLabel>
                <Input
                  value={formData.sourceUrl}
                  onChange={(e) => setFormData((prev) => ({ ...prev, sourceUrl: e.target.value }))}
                  placeholder='URL to the source platform page'
                />
              </FormControl>

              <FormControl>
                <FormLabel>Address</FormLabel>
                <Textarea
                  value={formData.address}
                  onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
                  placeholder='Enter full address'
                  rows={3}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Coordinates</FormLabel>
                <VStack spacing={3} align='stretch'>
                  <Input
                    value={`${formData.coordinates.lat},${formData.coordinates.lng}`}
                    onChange={(e) => {
                      const [lat, lng] = e.target.value.split(',');
                      const newLat = parseFloat(lat) || 0;
                      const newLng = parseFloat(lng) || 0;
                      setFormData((prev) => ({
                        ...prev,
                        coordinates: { lat: newLat, lng: newLng },
                      }));
                    }}
                    placeholder='lat,lng (e.g., 25.2048,55.2708)'
                  />

                  <InteractiveMap
                    lat={formData.coordinates.lat}
                    lng={formData.coordinates.lng}
                    onCoordinatesChange={(lat: number, lng: number) => {
                      setFormData((prev) => ({
                        ...prev,
                        coordinates: { lat, lng },
                      }));
                    }}
                    title={formData.name}
                    height='350px'
                    zoom={16}
                  />
                </VStack>
              </FormControl>

              <HStack spacing={4}>
                <FormControl>
                  <FormLabel>Phone Number</FormLabel>
                  <Input
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData((prev) => ({ ...prev, phoneNumber: e.target.value }))}
                    placeholder='Contact phone number'
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Website</FormLabel>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData((prev) => ({ ...prev, website: e.target.value }))}
                    placeholder='Website URL'
                  />
                </FormControl>
              </HStack>
            </VStack>

            {/* Ratings & Business Info */}
            <VStack spacing={4} align='stretch' p={4} border='1px' borderColor='gray.200' borderRadius='md'>
              <Text fontWeight='bold' color='gray.700'>
                Ratings & Business Information
              </Text>

              <HStack spacing={4}>
                <FormControl>
                  <FormLabel>Rating</FormLabel>
                  <NumberInput
                    value={formData.rating}
                    onChange={(_, valueAsNumber) =>
                      setFormData((prev) => ({ ...prev, rating: isNaN(valueAsNumber) ? '' : valueAsNumber }))
                    }
                    min={0}
                    max={5}
                    step={0.1}>
                    <NumberInputField placeholder='0.0' />
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>Ratings Total</FormLabel>
                  <NumberInput
                    value={formData.ratingsTotal}
                    onChange={(_, valueAsNumber) =>
                      setFormData((prev) => ({ ...prev, ratingsTotal: isNaN(valueAsNumber) ? '' : valueAsNumber }))
                    }>
                    <NumberInputField placeholder='Total number of ratings' />
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>Reviews Total</FormLabel>
                  <NumberInput
                    value={formData.reviewsTotal}
                    onChange={(_, valueAsNumber) =>
                      setFormData((prev) => ({ ...prev, reviewsTotal: isNaN(valueAsNumber) ? '' : valueAsNumber }))
                    }>
                    <NumberInputField placeholder='Total number of reviews' />
                  </NumberInput>
                </FormControl>
              </HStack>

              <HStack spacing={4}>
                <FormControl>
                  <FormLabel>Price Level</FormLabel>
                  <Select
                    value={formData.priceLevel}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, priceLevel: e.target.value ? Number(e.target.value) : '' }))
                    }>
                    <option value=''>Select price level</option>
                    <option value='1'>$ (Inexpensive)</option>
                    <option value='2'>$$ (Moderate)</option>
                    <option value='3'>$$$ (Expensive)</option>
                    <option value='4'>$$$$ (Very Expensive)</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Avg Cost Per Couple</FormLabel>
                  <NumberInput
                    value={formData.avgCostPerCouple}
                    onChange={(_, valueAsNumber) =>
                      setFormData((prev) => ({ ...prev, avgCostPerCouple: isNaN(valueAsNumber) ? '' : valueAsNumber }))
                    }>
                    <NumberInputField placeholder='Average cost' />
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>Currency</FormLabel>
                  <Input
                    value={formData.avgCostCurrency}
                    onChange={(e) => setFormData((prev) => ({ ...prev, avgCostCurrency: e.target.value }))}
                    placeholder='AED, USD, etc.'
                  />
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel>Business Status</FormLabel>
                <Select
                  value={formData.businessStatus}
                  onChange={(e) => setFormData((prev) => ({ ...prev, businessStatus: e.target.value }))}>
                  <option value=''>Select business status</option>
                  <option value='OPERATIONAL'>Operational</option>
                  <option value='CLOSED_TEMPORARILY'>Closed Temporarily</option>
                  <option value='CLOSED_PERMANENTLY'>Closed Permanently</option>
                </Select>
              </FormControl>
            </VStack>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant='ghost' mr={3} onClick={onClose} disabled={updateMutation.isLoading}>
            Cancel
          </Button>
          <Button
            colorScheme='blue'
            onClick={handleSubmit}
            isLoading={updateMutation.isLoading}
            loadingText='Updating...'>
            Update Location
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditSourceBrandLocationModal;
